import {useContext, useEffect, useMemo} from 'react';
import {Navigate, Route, RouterProvider, Routes, createBrowserRouter, createRoutesFromElements, useNavigate} from 'react-router-dom';
import {PluginPage} from './Pages/PluginPage/PluginPage';
import {useAppDispatch, useAppSelector} from '@core/Redux/hooks';
import {SigninPage} from './Pages/SigninPage/SigninPage';
import {DesktopAppLayout} from './AppLayouts/DesktopAppLayout/DesktopAppLayout';
import {addDataImportPath, addIntegrationPath, addReportPath, aiChatPath, exportDataPath, tablePath, profilePath, registrationPagePath, signinPagePath, signupPagePath, extensionSessionPagePath} from '@core/Constants/route-paths';
import {AccessTokenContext, AccessTokenDispatchContext} from '@core/Contexts/AccessTokenContext';
import {useLocalStorage} from '@core/Hooks/use-local-storage';
import {accessTokenKey} from '@core/Constants/app-storage-keys';
import {isMobile} from 'react-device-detect';
import {MobileAppLayout} from './AppLayouts/MobileAppLayout/MobileAppLayout';
import {OperationEventPoller} from '@core/ServiceComponents/OperationEventPoller/OperationEventPoller';
import {DeviceTypeContext, IDeviceType} from '@core/Contexts/DeviceTypeContext';
import {AiChatPage} from './Pages/AiChatPage/AiChatPage'
import urlJoin from 'url-join';
import {OrdersPage} from './Pages/OrdersPage/OrdersPage';
import {
    initApplicationAsync,
    initApplicationCore} from '@core/Redux/Slices/appSlice';
import {
    selectAppReady,
    selectPluginsState,
    selectTenantConfig,
    selectUserLogged,
    selectUserNotLogged
} from '@core/Redux/store';
import {serializeError} from 'serialize-error';
import Logger from 'js-logger';
import { EventSourcingStoreProvider } from '@core/Stores/EventSourcingStoreProvider';
import { RegistrationPage } from './Pages/RegistrationPage/RegistrationPage';
import { SignupPage } from './Pages/SignupPage/SignupPage';
import { AddReport } from './Pages/ReportsPage/AddReport';
import { AddIntegration } from './Pages/IntegrationPage/AddIntegration';
import { LeadImportPage } from './Pages/DataImportPage/LeadImportPage';
import { LeadExportPage } from './Pages/DataExportPage/LeadExportPage';
import { ProfilePage } from './Pages/ProfilePage/profilePage';
import { LandingPageV2 } from './Pages/LandingPage/LandingPageV2';
import { ExtensionSessionPage } from './Pages/ExtensionSessionPage/ExtensionSessionPage';
import {useTranslation} from "react-i18next";
import { loadedRoutes } from '@core/Plugins/pluginManager';
import { ConfigProvider, Spin } from 'antd';
import { locale } from 'src/Locale/date-fns';
import { PRIMARY_COLOR } from 'src/theme';
import { t } from 'i18next';
import { AutocompleteStoreProvider } from '@core/Stores/AutocompleteStoreProvider';
import { ITenantConfig } from '@core/Models/autogenerated/tenantConfig.models';
import { landingPagePath } from '@core/Constants/route-paths';

declare var location: any;

const AppLayout = (props: any) =>
{
    const dispatch = useAppDispatch();
    const deviceType = useContext(DeviceTypeContext);
    const appReady = useAppSelector(selectAppReady);
    const userLogged = useAppSelector(selectUserLogged);
    const userNotLogged = useAppSelector(selectUserNotLogged);

    const tenantConfig = useAppSelector(selectTenantConfig);

    const navigateTo = useNavigate();

    useEffect(() => {
        const initApp = async () => {
            await dispatch(initApplicationAsync());
        }

        initApp().catch(err => Logger.error('initApp failed', serializeError(err, { maxDepth: 2 })));

        return () => {};
    }, []);

    useEffect(()=> {
        if (userNotLogged)
            navigateTo(signinPagePath);
    }, [userNotLogged]);

    if (userLogged && appReady) {
        if (deviceType.isMobile)
            return <MobileAppLayout/>;
        else
            return <DesktopAppLayout/>;
    } else {
        return <Loading/>;
    }
}

function Loading() {
    return <div
        style={{
            height: "100%",
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",
            gap: "20px",
            color: PRIMARY_COLOR,
        }}
    >
        <Spin size='large'/>
        <span>{t("loading_status")}</span>
    </div>;
}

function NoRoute() {
    const [accessToken] = useLocalStorage<string>(accessTokenKey, {watchChanges: true});
    const appReady = useAppSelector(selectAppReady);
    const tenantConfig = useAppSelector(selectTenantConfig);
    const defaultTableId = getDefaultTableId(tenantConfig);
    if (defaultTableId == null) {
        return <span>There is no tables in configuration</span>;
    }

    if (appReady) {
        const getDefaultPath = () => accessToken
            ? tablePath(defaultTableId)
            : signinPagePath;
            
        return (<Navigate to={getDefaultPath()}/>);
    }
    else
        return (<></>);
}

function App() {
    const [accessToken, setAccessToken] = useLocalStorage<string>(accessTokenKey, {watchChanges: true});

    const isMobileDevice = process.env.REACT_APP_USE_MOBILE_APP_LAYOUT === 'true'
        ? true
        : isMobile;
    const deviceType: IDeviceType = {isMobile: isMobileDevice};

    let router = createBrowserRouter(
        createRoutesFromElements([
            (<Route path={landingPagePath} element={<LandingPageV2/>}/>),
            (<Route path={registrationPagePath} element={<RegistrationPage/>}/>),
            (<Route path={signupPagePath} element={<SignupPage/>}/>),
            (<Route path={signinPagePath} element={<SigninPage/>}/>),
            (<Route path={extensionSessionPagePath} element={<ExtensionSessionPage/>}/>),
            (<Route path='*' element={<CRM/>} />)
        ])
    );

    return <DeviceTypeContext.Provider value={deviceType}>
            <AccessTokenContext.Provider value={accessToken}>
                <AccessTokenDispatchContext.Provider value={setAccessToken as ((val: string | undefined) => void)}>
                    <ConfigProvider locale={locale} theme={{ token: { colorPrimary: PRIMARY_COLOR } }} componentSize="large">
                        <RouterProvider router={router} />
                    </ConfigProvider>
                </AccessTokenDispatchContext.Provider>
            </AccessTokenContext.Provider>
        </DeviceTypeContext.Provider>
            ;
}

function CRM() {
    const appReady = useAppSelector(selectAppReady);
    const tenantConfig = useAppSelector(selectTenantConfig);

    let _ = useMemo(()=>initApplicationCore(), []);

    const getTokenUserId = (serializedToken: string | null) => {
        try {
            const token = serializedToken ? JSON.parse(serializedToken) : null;
            const payload = JSON.parse(atob(token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/')));
            return payload['http://schemas.xmlsoap.org/ws/2005/05/identity/claims/nameidentifier'] ?? payload.sub ?? null;
        } catch {
            return null;
        }
    };

    // если в другой вкладке сменили пользователя, перезагружаем страницу.
    // нельзя, чтобы стейт заказов с одной учётки писался в другую
    // данные будут инконсистентны
    useEffect(()=>{
        const listener = (e: StorageEvent) => {
            if(e.key !== accessTokenKey)
                return;

            if (getTokenUserId(e.oldValue) === getTokenUserId(e.newValue) && getTokenUserId(e.newValue) !== null)
            {
                return;
            }

            window.location.reload();
        }
        window.addEventListener('storage', listener);

        return ()=>{
            window.removeEventListener('storage', listener);
        };
    }, []);

    const pluginsState = useAppSelector(selectPluginsState);

    const renderAppRoutes = () => {
        return <Routes>
                <Route path={registrationPagePath} element={<RegistrationPage/>}/>
                <Route path={signupPagePath} element={<SignupPage/>}/>
                <Route path={signinPagePath} element={<SigninPage/>}/>
                <Route element={<AppLayout/>}>
                    {tenantConfig?.tables.map(table => 
                        <Route key={'table' + table.tableId} path={tablePath(table.tableId)+'/*'} element={<OrdersPage tableId={table.tableId}/>}/>
                    )}
                    <Route path={aiChatPath} element={<AiChatPage/>}/>
                    <Route path={profilePath} element={<ProfilePage/>}/>
                    <Route path={addReportPath} element={<AddReport/>}/>
                    <Route path={addIntegrationPath} element={<AddIntegration/>}/>
                    <Route path={addDataImportPath} element={<LeadImportPage/>}/>
                    <Route path={exportDataPath} element={<LeadExportPage/>}/>

                    {pluginsState.modules.flatMap(module => 
                        module.installation.routes.map((route) => 
                            <Route key={route.id}
                                path={urlJoin(route.path, ':pageId?')}
                                element={<PluginPage route = {loadedRoutes[route.id ?? ""]}/>}/>
                        )
                    )}
                                                
                    <Route path='*' element={<NoRoute/>} />
                </Route>
            </Routes>;
    }

    return (
        <AutocompleteStoreProvider>
            <EventSourcingStoreProvider>
                    {renderAppRoutes()}
                    {appReady && <OperationEventPoller/>}
            </EventSourcingStoreProvider>
        </AutocompleteStoreProvider>
    );
}

export function getDefaultTableId(tenantConfig: ITenantConfig|null): string | undefined {
    return tenantConfig?.tables?.[0]?.tableId;
}

export default App;
